'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Bot, Send, MessageSquare, Paperclip, Mic, MicOff, Square,
  Zap, Wrench, Target, ChevronDown, Check,
} from 'lucide-react';
import { authFetch } from '@/lib/authFetch';
import DraftCard, { type DraftCardData } from './DraftCard';
import CustomerContextCard, { type CustomerContextData } from './CustomerContextCard';
import { ChartBlock, extractChartFences } from './ChartBlock';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3003';

// Pull any draft-card payloads out of an agent message's toolsUsed array.
// The server marks a tool result with _render: 'draft_card' when it's
// staging an external write (currently sms_send_draft) — see
// server/lib/tool-executor.js. We render those inline above the agent's
// free-text reply so the operator can approve or decline without leaving
// the chat.
//
// T2.1 (fix/draft-state-machine): pass `seenDraftIds` from the outer
// render walk. If a draft id has already been rendered in an earlier
// message (e.g. the agent re-emitted the same draft_id on turn 2 for
// any reason), skip it. This kills the "3 estimate cards stacked in
// chat" symptom Chris saw in Quinton's stream.
function extractDraftCards(
  toolsUsed?: ChatMessage['toolsUsed'],
  seenDraftIds?: Set<string>,
): DraftCardData[] {
  if (!toolsUsed) return [];
  const out: DraftCardData[] = [];
  for (const t of toolsUsed) {
    const r = t.result as Record<string, unknown> | undefined;
    if (r && r._render === 'draft_card' && r.draft_id && r.kind && r.payload) {
      const id = String(r.draft_id);
      if (seenDraftIds && seenDraftIds.has(id)) continue;
      if (seenDraftIds) seenDraftIds.add(id);
      out.push(r as unknown as DraftCardData);
    }
  }
  return out;
}

// customer_context_load returns the same _render marker convention so we
// render the bundle as a structured card (profile + tags + property +
// stats + recent estimates + memory facts + View in CRM) instead of
// letting the agent's plain-text summary stand alone.
function extractContextCards(toolsUsed?: ChatMessage['toolsUsed']): CustomerContextData[] {
  if (!toolsUsed) return [];
  const out: CustomerContextData[] = [];
  for (const t of toolsUsed) {
    const r = t.result as Record<string, unknown> | undefined;
    if (r && r._render === 'customer_context_card' && r.profile) {
      out.push(r as unknown as CustomerContextData);
    }
  }
  return out;
}

// ── Types ──────────────────────────────────────────────────────────────

interface CrmCustomer {
  id: string;
  cid?: string;
  name: string;
  email?: string;
}

interface ChatMessage {
  id: string;
  role: 'owner' | 'agent';
  content: string;
  timestamp: string;
  customerMention?: { id: string; name: string } | null;
  isThinking?: boolean;
  thinkingLabel?: string;
  images?: string[];
  toolsUsed?: { tool: string; input?: Record<string, unknown>; result?: unknown }[];
}

interface SlashCommand {
  id: string;
  name: string;
  description: string;
  prompt: string;
  source: 'manual' | 'default' | 'agent-created';
}

// ── Helpers ────────────────────────────────────────────────────────────

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function getInitials(name: string): string {
  const parts = (name || 'U').trim().split(/\s+/);
  if (parts.length === 1) return (parts[0][0] || 'U').toUpperCase();
  return ((parts[0][0] || '') + (parts[parts.length - 1][0] || '')).toUpperCase();
}

// Render text with markdown (bold, lists) and clickable links
function renderWithLinks(text: string) {
  if (!text) return null;
  text = text.replace(/\\u2022/g, '\u2022').replace(/\\u2013/g, '\u2013').replace(/\\u2014/g, ',');
  text = text.replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
  text = text.replace(/\u2014/g, ',').replace(/\u2013/g, ',');

  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];

  const processInline = (str: string, lineIdx: number): React.ReactNode[] => {
    const parts: React.ReactNode[] = [];
    const regex = /(\*\*(.+?)\*\*|\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s)]+))/g;
    let lastIndex = 0;
    let match;
    let k = 0;

    const makeLink = (url: string, label: string) => (
      <a
        key={`a${lineIdx}-${k++}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors"
        style={{ background: '#00C2A8', color: '#fff', textDecoration: 'none', marginLeft: 2, marginRight: 2 }}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
        </svg>
        {label}
      </a>
    );

    while ((match = regex.exec(str)) !== null) {
      if (match.index > lastIndex) parts.push(<span key={`t${lineIdx}-${k++}`}>{str.slice(lastIndex, match.index)}</span>);
      if (match[2]) {
        parts.push(<strong key={`b${lineIdx}-${k++}`}>{match[2]}</strong>);
      } else if (match[3] && match[4]) {
        const url = match[4];
        const linkText = match[3];
        const isCrmLink = url.includes('copilotcrm.com') || url.includes('home.works');
        parts.push(makeLink(url, isCrmLink ? 'View in CRM' : linkText));
      } else if (match[5]) {
        const url = match[5];
        const isCrmLink = url.includes('copilotcrm.com') || url.includes('home.works');
        parts.push(makeLink(url, isCrmLink ? 'View in CRM' : 'Open Link'));
      }
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < str.length) parts.push(<span key={`t${lineIdx}-${k++}`}>{str.slice(lastIndex)}</span>);
    return parts.length > 0 ? parts : [<span key={`t${lineIdx}-0`}>{str}</span>];
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    // Markdown headers: ## or ### → small uppercase teal label
    const headerMatch = line.match(/^\s*(#{2,4})\s+(.+)/);
    if (headerMatch) {
      elements.push(
        <div
          key={`l${i}`}
          className="text-[10px] font-bold text-[#00C2A8] uppercase tracking-[0.12em] mt-3 first:mt-0 mb-1.5"
        >
          {processInline(headerMatch[2], i)}
        </div>
      );
      continue;
    }
    const bulletMatch = line.match(/^\s*[-\u2022]\s+(.+)/);
    if (bulletMatch) {
      elements.push(
        <div key={`l${i}`} className="flex gap-1.5 ml-1 mt-0.5">
          <span className="flex-shrink-0">{'\u2022'}</span>
          <span>{processInline(bulletMatch[1], i)}</span>
        </div>
      );
      continue;
    }
    const numMatch = line.match(/^\s*(\d+)\.\s+(.+)/);
    if (numMatch) {
      // If this numbered item is a section header (ends with a colon, no
      // sentence content after it), render the whole title bold so lists
      // like "1. Customer Management:" stand out above their sub-bullets.
      const body = numMatch[2];
      const isSectionHeader = /^[^.!?]{3,60}:\s*$/.test(body.trim());
      elements.push(
        <div key={`l${i}`} className={`flex gap-1.5 ml-1 ${isSectionHeader ? 'mt-2 first:mt-0' : 'mt-0.5'}`}>
          <span className="flex-shrink-0 font-semibold">{numMatch[1]}.</span>
          {isSectionHeader
            ? <strong className="font-semibold">{processInline(body.replace(/:\s*$/, ''), i)}</strong>
            : <span>{processInline(body, i)}</span>
          }
        </div>
      );
      continue;
    }
    if (line.trim() === '') {
      elements.push(<div key={`l${i}`} className="h-1.5" />);
    } else {
      elements.push(<div key={`l${i}`}>{processInline(line, i)}</div>);
    }
  }
  return <>{elements}</>;
}

// Render an agent message that may contain ```chart-json fenced blocks.
// Splits the text around each fence; surrounding markdown still routes
// through renderWithLinks, fences become inline ChartBlock components.
function renderMessageContent(text: string) {
  const fences = extractChartFences(text || '');
  if (fences.length === 0) return renderWithLinks(text);
  const out: React.ReactNode[] = [];
  let cursor = 0;
  fences.forEach((f, idx) => {
    if (f.start > cursor) out.push(<div key={`pre-${idx}`}>{renderWithLinks(text.slice(cursor, f.start))}</div>);
    if (f.payload) out.push(<ChartBlock key={`chart-${idx}`} payload={f.payload} />);
    cursor = f.end;
  });
  if (cursor < text.length) out.push(<div key="tail">{renderWithLinks(text.slice(cursor))}</div>);
  return <>{out}</>;
}

// ── Component ──────────────────────────────────────────────────────────

interface Props {
  customers: CrmCustomer[];
  onAgentAction: () => void;
  tenantId?: string;
  onCustomerFocus?: (customerId: string | null, customerName?: string | null) => void;
}

function extractCustomerFocus(toolsUsed: unknown): { customerId: string | null; customerName: string | null } {
  if (!Array.isArray(toolsUsed)) return { customerId: null, customerName: null };
  for (let i = toolsUsed.length - 1; i >= 0; i--) {
    const t = toolsUsed[i] as { tool?: string; input?: Record<string, unknown>; result?: Record<string, unknown> };
    if (!t) continue;
    const input = t.input || {};
    const result = t.result || {};
    const cid = (input.customerId as string) || (input.cid as string) || (result.customerId as string) || (result.cid as string);
    if (cid) {
      const name = (input.customerName as string) || (result.name as string) || (result.customerName as string) || null;
      return { customerId: String(cid), customerName: name };
    }
  }
  return { customerId: null, customerName: null };
}

type AgentMode = 'auto' | 'operations' | 'strategy';

const AGENT_MODE_META: Record<AgentMode, { label: string; description: string; icon: typeof Zap }> = {
  auto: {
    label: 'Auto',
    description: 'Agent picks the right behavior based on what you ask.',
    icon: Zap,
  },
  operations: {
    label: 'Operations',
    description: 'Just execute. No strategic recommendations.',
    icon: Wrench,
  },
  strategy: {
    label: 'Strategy',
    description: 'CEO coach mode. Recommendations grounded in your numbers.',
    icon: Target,
  },
};

// Hard cap on how many chat messages we mirror to localStorage. Quota on most
// browsers is ~5 MB per origin and a runaway tool-result blob plus dozens of
// turns can blow past that. 200 turns covers any reasonable session.
const CHAT_HISTORY_CAP = 200;
// Byte cap on the localStorage blob. Trim oldest messages (slice from front)
// until the serialised value fits. 4 MB leaves ~1 MB headroom below the
// typical 5 MB browser quota even after other per-origin keys are counted.
const CHAT_STORAGE_MAX_BYTES = 4 * 1024 * 1024;

function trimToByteCap(msgs: ChatMessage[]): ChatMessage[] {
  let serialized = JSON.stringify(msgs);
  while (serialized.length > CHAT_STORAGE_MAX_BYTES && msgs.length > 1) {
    // Drop the oldest message (index 0) until we fit under the cap.
    msgs = msgs.slice(1);
    serialized = JSON.stringify(msgs);
    if (msgs.length === 1) break; // always keep at least the latest turn
  }
  if (serialized.length > CHAT_STORAGE_MAX_BYTES) {
    // Single message is still oversized (pathological tool-result blob).
    // Log a warning and return empty so we don't throw QuotaExceededError.
    console.warn('[AgentChat] Single message exceeds 4 MB localStorage cap — clearing history');
    return [];
  }
  return msgs;
}

export default function AgentChat({ customers, onAgentAction, tenantId, onCustomerFocus }: Props) {
  const storageKey = tenantId ? `crewsync_ops_chat_${tenantId}` : 'crewsync_ops_chat_v1';
  const modeStorageKey = tenantId ? `crewsync_agent_mode_${tenantId}` : 'crewsync_agent_mode_v1';
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = JSON.parse(localStorage.getItem(storageKey) || '[]');
      if (!Array.isArray(raw)) return [];
      // One-time cleanup: trim by count cap and byte cap, write back.
      const trimmed = trimToByteCap(raw.slice(-CHAT_HISTORY_CAP));
      if (trimmed.length !== raw.length) {
        try { localStorage.setItem(storageKey, JSON.stringify(trimmed)); } catch {}
      }
      return trimmed;
    } catch { return []; }
  });
  const [agentMode, setAgentMode] = useState<AgentMode>(() => {
    if (typeof window === 'undefined') return 'auto';
    try {
      const v = localStorage.getItem(modeStorageKey);
      if (v === 'operations' || v === 'strategy' || v === 'auto') return v;
    } catch {}
    return 'auto';
  });
  const [modePickerOpen, setModePickerOpen] = useState(false);
  // Slash command library + autocomplete state. Fetched on mount, refreshed
  // when the agent calls slash_command_create / slash_command_delete (we
  // watch the toolsUsed array on each agent reply) and on window focus
  // (covers the case where another tab created a command).
  const [slashCommands, setSlashCommands] = useState<SlashCommand[]>([]);
  const [slashOpen, setSlashOpen] = useState(false);
  const [slashQuery, setSlashQuery] = useState('');
  const [slashIndex, setSlashIndex] = useState(0);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionOpen, setMentionOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [recording, setRecording] = useState(false);
  const [micPaused, setMicPaused] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  // Imperative pull() handle so the send-handler can trigger an immediate
  // rehydrate after res.ok instead of waiting up to 20s for the next poll.
  // Without this, the optimistic user+agent local messages age out of the
  // 60s grace window before the next interval pull arrives, and the chat
  // visibly blinks empty for several seconds.
  const pullRef = useRef<(() => Promise<void>) | null>(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (blob.size < 100) return;
        const formData = new FormData();
        formData.append('audio', blob, 'voice.webm');
        try {
          const res = await authFetch(`${API_URL}/api/chat/transcribe`, { method: 'POST', body: formData });
          if (res.ok) {
            const data = await res.json();
            if (data.text) {
              handleInput(input ? input + ' ' + data.text : data.text);
              inputRef.current?.focus();
            }
          }
        } catch (err) { console.error('[voice] Transcribe failed:', err); }
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecording(true);
      setMicPaused(false);
    } catch (err) { console.error('[voice] Mic access denied:', err); }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
    setMicPaused(false);
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
      setMicPaused(true);
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
      setMicPaused(false);
    }
  };

  const stuckToBottomRef = useRef(true);

  const scrollToBottom = useCallback(() => {
    const c = messagesContainerRef.current;
    if (!c) return;
    c.scrollTop = c.scrollHeight;
  }, []);

  useEffect(() => {
    const c = messagesContainerRef.current;
    if (!c) return;
    const onUserIntent = () => { stuckToBottomRef.current = false; };
    const onScroll = () => {
      const distanceFromBottom = c.scrollHeight - c.scrollTop - c.clientHeight;
      if (distanceFromBottom < 30) stuckToBottomRef.current = true;
    };
    c.addEventListener('wheel', onUserIntent, { passive: true });
    c.addEventListener('touchmove', onUserIntent, { passive: true });
    c.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      c.removeEventListener('wheel', onUserIntent);
      c.removeEventListener('touchmove', onUserIntent);
      c.removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(() => {
    if (stuckToBottomRef.current) scrollToBottom();
  }, [messages, scrollToBottom]);

  // Clipboard paste support for images
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          if (!file) continue;
          if (file.size > 50 * 1024 * 1024) { alert('Image must be under 50MB'); return; }
          const reader = new FileReader();
          reader.onload = () => setSelectedImages(prev => [...prev, reader.result as string].slice(0, 10));
          reader.readAsDataURL(file);
          return;
        }
      }
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const capped = trimToByteCap(messages.slice(-CHAT_HISTORY_CAP));
      localStorage.setItem(storageKey, JSON.stringify(capped));
    } catch { /* QuotaExceededError or private-mode write block */ }
  }, [messages, storageKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try { localStorage.setItem(modeStorageKey, agentMode); } catch {}
  }, [agentMode, modeStorageKey]);

  // Cross-device chat sync — hydrate from the server's persistent thread on
  // mount and poll for new messages every 20s. The server is the source of
  // truth; localStorage is just an offline cache. This makes the chat feel
  // like iMessage — open it on the phone, see the same conversation on
  // desktop and vice versa.
  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;

    async function pull() {
      try {
        const res = await authFetch(`${API_URL}/api/agent-messages?thread=main&limit=200`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        // Main Chat shows free-form conversation only. Briefings
        // (source='morning-briefing') live in their own channel; including
        // them here clutters the feed mid-conversation every time a new
        // briefing fires. The TodaysFocusCard banner above the chat shows
        // the latest briefing summary; the detail will live in a dedicated
        // channel view once that lands.
        const serverMsgs: ChatMessage[] = (data.messages || [])
          .filter((m: { role: string; source?: string | null }) =>
            (m.role === 'user' || m.role === 'agent') &&
            m.source !== 'morning-briefing'
          )
          .map((m: {
            id: string;
            role: string;
            content: string;
            created_at: string;
            metadata?: { tools_used?: ChatMessage['toolsUsed'] };
          }) => ({
            id: `srv-${m.id}`,
            role: m.role === 'user' ? 'owner' as const : 'agent' as const,
            content: m.content,
            timestamp: m.created_at,
            // Preserve toolsUsed from server metadata so reloaded messages
            // still surface their draft cards on a fresh page load.
            toolsUsed: Array.isArray(m.metadata?.tools_used) ? m.metadata!.tools_used : undefined,
          }));

        if (cancelled) return;

        setMessages(prev => {
          // Server is canonical. Keep local-only messages added in the last
          // 60 seconds so in-flight sends + the thinking placeholder + the
          // optimistic agent reply don't get wiped before the server write
          // has propagated through the pull. The old 5s window was way too
          // short — sends legitimately take 5-30s to complete, and the poll
          // itself runs on a 20s cadence, so optimistic messages were aging
          // out of the local-only set before the server version arrived,
          // leaving a visible gap where the user's own message vanished.
          //
          // If the server returns empty (no persisted messages yet), don't
          // wipe local state at all — that would erase the user's in-flight
          // turn.
          if (serverMsgs.length === 0) return prev;
          const cutoff = Date.now() - 60_000;
          // Error / timeout messages (id prefix `agent-err-`) are injected
          // locally and never persisted to agent_messages. The old 60s
          // cutoff aged them out, so the operator would see "Taking longer
          // than expected" briefly and then the message would silently
          // disappear — making it look like the chat was "randomly
          // deleting messages" (Chris 2026-04-30). Keep them forever in
          // the local feed so the operator has a permanent record of any
          // error / timeout the chat reported.
          const localOnly = prev.filter(m => {
            if (m.id.startsWith('srv-')) return false;
            if (m.id.startsWith('agent-err-')) return true;
            // Owner messages (operator typed) must NEVER age out. The
            // optimistic local copy stays until either a matching srv-* row
            // arrives (dedupe drops the local) or the operator clears the
            // chat. Without this exemption, any operator message older than
            // 60s vanishes if the server is slow to echo it — which Chris
            // hit on 2026-04-30 ("If I type it, it's there. Why is it
            // deleting off my screen?"). Same logic as agent-err-: a
            // visible message that the user produced should never silently
            // disappear without an explicit signal.
            if (m.id.startsWith('owner-')) return true;
            return new Date(m.timestamp).getTime() > cutoff;
          });
          // Dedupe: if a local-only optimistic message matches a server msg
          // by role+content+close timestamp (<90s apart), drop the local
          // copy so we don't render it twice side-by-side with the srv-* row.
          const dedupedLocal = localOnly.filter(local => {
            return !serverMsgs.some(srv =>
              srv.role === local.role &&
              srv.content.trim() === local.content.trim() &&
              Math.abs(new Date(srv.timestamp).getTime() - new Date(local.timestamp).getTime()) < 90_000
            );
          });
          const merged = [...serverMsgs, ...dedupedLocal]
            .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
          return merged;
        });
      } catch { /* offline — keep showing localStorage cache */ }
    }

    pullRef.current = pull;
    pull();
    const interval = setInterval(pull, 20_000);
    return () => { cancelled = true; clearInterval(interval); pullRef.current = null; };
  }, [tenantId]);

  // Slash command library: fetch on mount, refresh on window focus
  // (covers cross-tab/cross-device staleness — another tab created a
  // command, this one comes back from a tab switch and reflects it).
  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    const fetchCommands = async () => {
      try {
        const res = await authFetch(`${API_URL}/api/slash-commands`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        if (Array.isArray(data?.commands)) setSlashCommands(data.commands);
      } catch { /* offline — keep what's in state */ }
    };
    fetchCommands();
    window.addEventListener('focus', fetchCommands);
    return () => { cancelled = true; window.removeEventListener('focus', fetchCommands); };
  }, [tenantId]);

  const mentionResults = (() => {
    if (!mentionQuery) return [];
    const q = mentionQuery.toLowerCase();
    return customers.filter(c => c.name?.toLowerCase().includes(q)).slice(0, 6);
  })();

  // Slash autocomplete results. Filter by prefix (operator-typed) so the
  // dropdown narrows as they type; cap at 8 entries for mobile readability.
  const slashResults = (() => {
    if (!slashOpen) return [];
    const q = slashQuery.toLowerCase();
    return slashCommands
      .filter(c => !q || c.name.startsWith(q))
      .slice(0, 8);
  })();

  // Strict detection: only open the slash dropdown when input is exactly
  // "/" or "/<name-fragment>" with no spaces. Voice transcripts and pasted
  // URLs ("/private/path") will never satisfy this so they fall through
  // unchanged. Anchored regex per the validator's recommendation.
  const handleInput = (val: string) => {
    setInput(val);
    if (/^\/[a-z0-9-]*$/.test(val)) {
      setSlashQuery(val.slice(1));
      setSlashOpen(true);
      setSlashIndex(0);
      setMentionOpen(false);
      setMentionQuery('');
      return;
    }
    setSlashOpen(false);
    setSlashQuery('');
    const lastAt = val.lastIndexOf('@');
    if (lastAt >= 0) {
      const afterAt = val.slice(lastAt + 1);
      if (!afterAt.includes(' ') || afterAt.length < 20) {
        setMentionQuery(afterAt);
        setMentionOpen(true);
        return;
      }
    }
    setMentionOpen(false);
    setMentionQuery('');
  };

  const insertMention = (customer: CrmCustomer) => {
    const lastAt = input.lastIndexOf('@');
    const before = input.slice(0, lastAt);
    setInput(`${before}@${customer.name} `);
    setMentionOpen(false);
    setMentionQuery('');
    inputRef.current?.focus();
  };

  // Selecting a slash command inserts the literal "/name " into the textarea.
  // The server expands /name into the full prompt text in main-chat.js
  // before invoking the agent loop. Frontend stays dumb to the prompt body
  // so chat history shows a clean "/morning" bubble instead of a 200-char
  // wall of expanded text.
  const insertSlashCommand = (cmd: SlashCommand) => {
    setInput(`/${cmd.name} `);
    setSlashOpen(false);
    setSlashQuery('');
    setSlashIndex(0);
    inputRef.current?.focus();
  };

  const sendMessage = async (directText?: string) => {
    const text = (directText || input).trim();
    if ((!text && selectedImages.length === 0) || sending) return;

    let mentionedCustomer: { id: string; name: string } | null = null;
    const mentionMatch = text.match(/@([\w\s]+)/);
    if (mentionMatch) {
      const mentionedName = mentionMatch[1].trim();
      const customer = customers.find(c => c.name.toLowerCase().includes(mentionedName.toLowerCase()));
      if (customer) {
        mentionedCustomer = { id: customer.cid || customer.id, name: customer.name };
      }
    }

    const ownerMsg: ChatMessage = {
      id: `owner-${Date.now()}`,
      role: 'owner',
      content: text,
      timestamp: new Date().toISOString(),
      customerMention: mentionedCustomer,
      images: selectedImages.length > 0 ? [...selectedImages] : undefined,
    };

    setMessages(prev => [...prev, ownerMsg]);
    setInput('');
    setSelectedImages([]);
    setMentionOpen(false);
    if (inputRef.current) inputRef.current.style.height = 'auto';
    setSending(true);

    const historyForApi = messages.slice(-10).map(m => ({
      role: m.role === 'owner' ? 'user' : 'assistant',
      content: m.content,
    }));

    const payload: Record<string, unknown> = {
      content: text,
      history: historyForApi,
      context: 'operations',
      agentMode,
      images: selectedImages.length > 0 ? selectedImages : undefined,
      ...(mentionedCustomer ? {
        customerId: mentionedCustomer.id,
        customerName: mentionedCustomer.name,
        mentions: [mentionedCustomer],
      } : {}),
    };

    const thinkingId = `thinking-${Date.now()}`;
    setMessages(prev => [...prev, {
      id: thinkingId,
      role: 'agent' as const,
      content: '...',
      timestamp: new Date().toISOString(),
      isThinking: true,
      thinkingLabel: 'Agent is working...',
    }]);

    // Evolve the thinking label over time so the user knows we're still going.
    // First message can take 30-60s to warm up the agent context + tools.
    const labelTimers = [
      setTimeout(() => {
        setMessages(prev => prev.map(m => m.id === thinkingId
          ? { ...m, thinkingLabel: 'Thinking — loading your data...' } : m));
      }, 15000),
      setTimeout(() => {
        setMessages(prev => prev.map(m => m.id === thinkingId
          ? { ...m, thinkingLabel: 'Still working — first message takes longer.' } : m));
      }, 35000),
      setTimeout(() => {
        setMessages(prev => prev.map(m => m.id === thinkingId
          ? { ...m, thinkingLabel: 'Almost there, finalizing reply...' } : m));
      }, 70000),
    ];

    const controller = new AbortController();
    // Hard safety net: always clear typing state after 115s no matter what
    const hardStopTimer = setTimeout(() => {
      try { controller.abort(); } catch {}
      setMessages(prev => prev.filter(m => m.id !== thinkingId));
      setMessages(prev => [...prev, {
        id: `agent-err-${Date.now()}`,
        role: 'agent' as const,
        content: 'Taking longer than expected. The agent may still be working - check the activity feed in a moment.',
        timestamp: new Date().toISOString(),
      }]);
      setSending(false);
    }, 115000);
    const timer = setTimeout(() => controller.abort(), 110000);
    try {
      const res = await authFetch(`${API_URL}/api/threads/main/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      clearTimeout(timer);
      setMessages(prev => prev.filter(m => m.id !== thinkingId));
      if (res.ok) {
        const data = await res.json();
        const reply = (data.content || data.message || '').trim() || JSON.stringify(data);
        const toolsUsed = Array.isArray(data.toolsUsed) ? data.toolsUsed : undefined;
        const agentMsg: ChatMessage = {
          id: `agent-${Date.now()}`,
          role: 'agent',
          content: reply,
          timestamp: new Date().toISOString(),
          toolsUsed,
        };
        setMessages(prev => [...prev, agentMsg]);
        onAgentAction();
        // Phase 3D: notify parent of customer focus shifts so the right-hand
        // sidebar pins to the customer the agent just acted on.
        if (onCustomerFocus) {
          const { customerId: focusCid, customerName: focusName } = extractCustomerFocus(toolsUsed);
          if (focusCid) onCustomerFocus(focusCid, focusName);
        }
        // If the agent created or deleted a slash command this turn, refetch
        // the library so the dropdown reflects it on the next "/" the user
        // types. Tool result carries _slashCommandsChanged: true (set by
        // tool-executor for slash_command_create / slash_command_delete).
        const slashChanged = (toolsUsed || []).some((t: { result?: unknown }) => {
          const r = t.result as Record<string, unknown> | undefined;
          return !!(r && r._slashCommandsChanged === true);
        });
        if (slashChanged) {
          authFetch(`${API_URL}/api/slash-commands`)
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (Array.isArray(d?.commands)) setSlashCommands(d.commands); })
            .catch(() => { /* best effort */ });
        }
        // Immediately re-pull from the server now that the agent turn has
        // been persisted into agent_messages (thread=main, source=chat) by
        // /threads/main/message. Without this the UI waits up to 20s for
        // the next interval pull, during which the optimistic local msgs
        // can blink out as they age past the grace window.
        if (pullRef.current) { pullRef.current().catch(() => {}); }
      } else {
        setMessages(prev => [...prev, {
          id: `agent-err-${Date.now()}`,
          role: 'agent' as const,
          content: 'Something went wrong. Please try again.',
          timestamp: new Date().toISOString(),
        }]);
      }
    } catch {
      clearTimeout(timer);
      setMessages(prev => prev.filter(m => m.id !== thinkingId));
      setMessages(prev => [...prev, {
        id: `agent-err-${Date.now()}`,
        role: 'agent' as const,
        content: 'Taking longer than expected. The agent may still be working - check the activity feed in a moment.',
        timestamp: new Date().toISOString(),
      }]);
    } finally {
      clearTimeout(timer);
      clearTimeout(hardStopTimer);
      labelTimers.forEach(clearTimeout);
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Chat header */}
      <div className="flex items-center gap-2 mb-2 shrink-0">
        <MessageSquare className="w-3.5 h-3.5 text-[#00C2A8]" />
        <p className="text-xs font-bold text-[--text-secondary] uppercase tracking-wider">Agent Chat</p>
        <button
          onClick={async () => {
            if (!confirm('Start a new chat? Your current thread is archived (support can still review it) and you get a clean slate here.')) return;
            // Clear local state immediately so the UI feels snappy; then
            // archive the server-side thread (rotates thread_id) so the 20s
            // pull doesn't rehydrate the briefing cards. The history stays in
            // Supabase under the archived thread_id so support can review it.
            setMessages([]);
            setAgentMode('auto');
            try { localStorage.removeItem(storageKey); } catch {}
            try { localStorage.removeItem(modeStorageKey); } catch {}
            try {
              await authFetch(`${API_URL}/api/agent-messages/clear?thread=main`, { method: 'DELETE' });
            } catch { /* best effort — local clear still applied */ }
          }}
          className="ml-auto text-[10px] text-[--text-secondary] hover:text-[#00C2A8] transition-colors"
          title="Archive current thread and start a new one (history preserved for support)"
        >
          New Chat
        </button>
      </div>

      {/* Chat container */}
      <div
        className="flex-1 min-h-0 rounded-xl border border-[--border-primary] flex flex-col overflow-hidden"
        style={{ background: 'var(--bg-card)' }}
      >
        {/* Messages area — overscroll-contain so swiping up at the top of
            the message list doesn't bubble up and scroll the entire page on
            mobile (which would push the composer off-screen). */}
        <div ref={messagesContainerRef} className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 py-3 space-y-3">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center px-4 py-8">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center mb-3"
                style={{ background: 'rgba(0, 194, 168, 0.1)' }}
              >
                <Bot className="w-5 h-5 text-[#00C2A8]" />
              </div>
              <p className="text-sm font-semibold text-[--text-primary] mb-1">Talk to your agent</p>
              <p className="text-[12px] text-[--text-secondary] leading-relaxed max-w-[220px]">
                Schedule visits, send estimates, text customers, add notes. Use @ to mention a customer.
              </p>
            </div>
          )}

          {/* T2.1 (fix/draft-state-machine): track every draft_id we've
              already rendered in THIS pass so a re-emitted result on a later
              message doesn't stack a duplicate card. Walks oldest-to-newest
              (the messages array order). The Set is fresh per render. */}
          {((seenDraftIds: Set<string>) => messages.map(msg => {
            if (msg.role === 'owner') {
              return (
                <div key={msg.id} className="flex justify-end">
                  <div className="max-w-[85%]">
                    {msg.images && msg.images.map((img, idx) => (
                      <img key={idx} src={img} alt={`attached ${idx + 1}`} className="max-w-full rounded-lg mb-1" style={{ maxHeight: '200px' }} />
                    ))}
                    <div
                      className="px-3 py-2 rounded-xl rounded-br-sm text-[13px] text-white leading-relaxed"
                      style={{ background: '#00C2A8' }}
                    >
                      {msg.content}
                    </div>
                    <p className="text-[9px] text-[--text-secondary] text-right mt-0.5 mr-1">
                      {relativeTime(msg.timestamp)}
                    </p>
                  </div>
                </div>
              );
            }
            return (
              <div key={msg.id} className="flex justify-start">
                <div className="max-w-[85%] flex gap-2">
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                    style={{ background: 'rgba(0, 194, 168, 0.1)' }}
                  >
                    <Bot className="w-3.5 h-3.5 text-[#00C2A8]" />
                  </div>
                  <div>
                    <div
                      className="px-3.5 py-2.5 rounded-2xl rounded-bl-md text-[13px] leading-relaxed"
                      style={{
                        background: 'var(--bg-card)',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                        color: 'var(--text-primary)',
                      }}
                    >
                      {msg.isThinking ? (
                        <span className="flex gap-1.5 items-center py-0.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#00C2A8] animate-bounce" style={{animationDelay:'0ms'}} />
                          <span className="w-1.5 h-1.5 rounded-full bg-[#00C2A8] animate-bounce" style={{animationDelay:'150ms'}} />
                          <span className="w-1.5 h-1.5 rounded-full bg-[#00C2A8] animate-bounce" style={{animationDelay:'300ms'}} />
                          <span className="text-[11px] text-[--text-secondary] ml-1">{msg.thinkingLabel || 'Thinking...'}</span>
                        </span>
                      ) : (
                        <>
                          {msg.toolsUsed && msg.toolsUsed.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-1.5">
                              {msg.toolsUsed.map((t, i) => (
                                <span key={i} className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded" style={{ background: 'rgba(0, 194, 168, 0.1)', color: '#00C2A8' }}>
                                  ⚡ {t.tool.replace(/_/g, ' ')}
                                </span>
                              ))}
                            </div>
                          )}
                          {extractContextCards(msg.toolsUsed).map((c, i) => (
                            <CustomerContextCard key={`ctx-${c.customerId}-${i}`} data={c} />
                          ))}
                          {extractDraftCards(msg.toolsUsed, seenDraftIds).map(d => (
                            <DraftCard key={d.draft_id} data={d} />
                          ))}
                          {renderMessageContent(msg.content)}
                        </>
                      )}
                    </div>
                    <p className="text-[9px] text-[--text-secondary] mt-0.5 ml-1">
                      {relativeTime(msg.timestamp)}
                    </p>
                  </div>
                </div>
              </div>
            );
          }))(new Set<string>())}
          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="shrink-0 border-t border-[--border-primary] px-3 py-2.5 relative">
          {/* Mention autocomplete */}
          {mentionOpen && mentionResults.length > 0 && (
            <div
              className="absolute bottom-full left-3 right-3 mb-1 rounded-lg border border-[--border-primary] overflow-hidden z-50"
              style={{ background: 'var(--bg-card)', boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}
            >
              {mentionResults.map(c => (
                <button
                  key={c.id}
                  onClick={() => insertMention(c)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[--text-primary] hover:bg-[--bg-main] transition-colors min-h-[40px]"
                  style={{ touchAction: 'manipulation' }}
                >
                  <div
                    className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[8px] font-bold shrink-0"
                    style={{ background: '#00C2A8' }}
                  >
                    {getInitials(c.name)}
                  </div>
                  <span className="text-[13px] font-medium truncate">{c.name}</span>
                </button>
              ))}
            </div>
          )}

          {/* Slash command autocomplete. Mutually exclusive with the mention
              dropdown (handleInput keeps only one open). Renders name +
              description per row; the prompt body never shows in the dropdown
              (mobile readability + keeps the picker compact). */}
          {slashOpen && (
            <div
              className="absolute bottom-full left-3 right-3 mb-1 rounded-lg border border-[--border-primary] overflow-hidden z-50"
              style={{ background: 'var(--bg-card)', boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}
              role="listbox"
            >
              <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-[--text-secondary] border-b border-[--border-primary]">
                Slash commands
              </div>
              {slashResults.length === 0 ? (
                <div className="px-3 py-3 text-[12px] text-[--text-secondary]">
                  No commands match. Ask the agent to create one (&ldquo;create a /{slashQuery || 'name'} command that...&rdquo;).
                </div>
              ) : slashResults.map((c, idx) => {
                const active = idx === slashIndex;
                return (
                  <button
                    key={c.id}
                    onClick={() => insertSlashCommand(c)}
                    onMouseEnter={() => setSlashIndex(idx)}
                    className={`w-full flex items-start gap-2 px-3 py-2 text-left transition-colors min-h-[44px] ${active ? 'bg-[#00C2A8]/10' : 'hover:bg-[--bg-main]'}`}
                    style={{ touchAction: 'manipulation' }}
                    role="option"
                    aria-selected={active}
                  >
                    <span
                      className="text-[12px] font-mono font-semibold shrink-0 mt-[1px]"
                      style={{ color: '#00C2A8' }}
                    >
                      /{c.name}
                    </span>
                    <span className="text-[12px] text-[--text-secondary] truncate flex-1 leading-snug">
                      {c.description || ' '}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {selectedImages.length > 0 && (
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {selectedImages.map((img, idx) => (
                <div key={idx} className="relative">
                  <img src={img} alt={`preview ${idx + 1}`} className="w-16 h-16 object-cover rounded-lg border border-[--border-primary]" />
                  <button
                    onClick={() => setSelectedImages(prev => prev.filter((_, i) => i !== idx))}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center text-xs"
                  >&times;</button>
                </div>
              ))}
              <span className="text-[11px] text-[--text-secondary]">{selectedImages.length}/10 images</span>
            </div>
          )}

          {/* Two-row composer: textarea + mic on top, paperclip / mode chip /
              send on the bottom. Modeled after VS Code Copilot's chat composer
              so the controls sit clearly below the message and don't crowd the
              text. Mic stays inline with the textarea so voice input remains
              one tap away. */}
          <div
            className="rounded-2xl px-2 py-1.5 transition-all"
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border-primary)',
              boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            }}
          >
            {/* Top row: textarea + mic / recording controls */}
            <div className="flex items-end gap-1.5">
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => { handleInput(e.target.value); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 140) + 'px'; }}
                onKeyDown={e => {
                  // Slash dropdown takes precedence: ArrowUp/Down/Enter
                  // navigate + select the highlighted command, Escape closes.
                  // Must preventDefault BEFORE the Enter-to-send branch so
                  // hitting Enter on a highlighted command doesn't also
                  // submit the literal "/morning" before insertion runs.
                  if (slashOpen && slashResults.length > 0) {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setSlashIndex(i => Math.min(slashResults.length - 1, i + 1));
                      return;
                    }
                    if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setSlashIndex(i => Math.max(0, i - 1));
                      return;
                    }
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      const pick = slashResults[slashIndex] || slashResults[0];
                      if (pick) insertSlashCommand(pick);
                      return;
                    }
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      setSlashOpen(false);
                      return;
                    }
                  }
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
                  if (e.key === 'Escape') { setMentionOpen(false); setSlashOpen(false); }
                }}
                placeholder={recording ? (micPaused ? 'Paused...' : 'Recording...') : 'Ask your agent anything...'}
                className="flex-1 text-[15px] md:text-[13px] bg-transparent text-[--text-primary] py-2 px-1 focus:outline-none resize-none leading-snug"
                style={{ minHeight: '40px', maxHeight: '140px', overflowY: 'auto' }}
                disabled={sending}
                rows={1}
              />
              {recording ? (
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={micPaused ? resumeRecording : pauseRecording}
                    className="w-9 h-9 rounded-full flex items-center justify-center transition-all"
                    style={{ background: micPaused ? 'rgba(245,158,11,0.1)' : 'rgba(0,194,168,0.1)' }}
                    title={micPaused ? 'Resume' : 'Pause'}
                    aria-label={micPaused ? 'Resume recording' : 'Pause recording'}
                  >
                    {micPaused
                      ? <Mic className="w-[18px] h-[18px]" style={{ color: '#f59e0b' }} />
                      : <MicOff className="w-[18px] h-[18px]" style={{ color: '#00C2A8' }} />
                    }
                  </button>
                  <button
                    onClick={stopRecording}
                    className="w-9 h-9 rounded-full flex items-center justify-center transition-all"
                    style={{ background: 'rgba(239,68,68,0.1)' }}
                    title="Stop and transcribe"
                    aria-label="Stop recording and transcribe"
                  >
                    <Square className="w-3.5 h-3.5" style={{ color: '#ef4444' }} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={startRecording}
                  className="w-9 h-9 rounded-full flex items-center justify-center transition-all hover:bg-[--bg-main] shrink-0"
                  disabled={sending}
                  title="Voice input"
                  aria-label="Start voice input"
                >
                  <Mic className="w-[18px] h-[18px] text-[--text-secondary]" />
                </button>
              )}
            </div>

            {/* Bottom row: paperclip on the left, mode chip + send on the right */}
            <div className="flex items-center justify-between mt-1">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-9 h-9 rounded-full flex items-center justify-center transition-all hover:bg-[--bg-main] shrink-0"
                disabled={sending || recording}
                title="Attach image"
                aria-label="Attach image"
              >
                <Paperclip className="w-[18px] h-[18px] text-[--text-secondary]" />
              </button>

              <div className="flex items-center gap-1.5">
                {/* Mode chip + popover. Sticky per-tenant in localStorage,
                    resets to 'auto' on New Chat. Anchor is relative so the
                    popover can position above it via bottom-full. */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setModePickerOpen(v => !v)}
                    className="flex items-center gap-1.5 px-2 py-1.5 rounded-md text-[12px] font-medium text-[--text-secondary] hover:bg-[--bg-main] transition-colors"
                    title="Switch agent mode"
                    aria-label="Switch agent mode"
                    aria-expanded={modePickerOpen}
                  >
                    {(() => {
                      const Icon = AGENT_MODE_META[agentMode].icon;
                      return <Icon className="w-3.5 h-3.5 text-[#00C2A8]" />;
                    })()}
                    <span>{AGENT_MODE_META[agentMode].label} mode</span>
                    <ChevronDown className={`w-3 h-3 transition-transform ${modePickerOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {modePickerOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setModePickerOpen(false)} />
                      <div
                        className="absolute right-0 bottom-full mb-2 w-72 rounded-lg border border-[--border-primary] shadow-lg z-50 overflow-hidden"
                        style={{ background: 'var(--bg-card)' }}
                        role="menu"
                      >
                        <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-[--text-secondary] border-b border-[--border-primary]">
                          Agent mode
                        </div>
                        {(['auto', 'operations', 'strategy'] as AgentMode[]).map(m => {
                          const meta = AGENT_MODE_META[m];
                          const Icon = meta.icon;
                          const active = agentMode === m;
                          return (
                            <button
                              key={m}
                              type="button"
                              onClick={() => { setAgentMode(m); setModePickerOpen(false); }}
                              className={`w-full flex items-start gap-2.5 px-3 py-2.5 text-left transition-colors ${active ? 'bg-[#00C2A8]/10' : 'hover:bg-[--bg-main]'}`}
                              role="menuitemradio"
                              aria-checked={active}
                            >
                              <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${active ? 'text-[#00C2A8]' : 'text-[--text-secondary]'}`} />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[12px] font-semibold text-[--text-primary]">{meta.label}</span>
                                  {active && <Check className="w-3 h-3 text-[#00C2A8]" />}
                                </div>
                                <div className="text-[11px] text-[--text-secondary] leading-snug mt-0.5">{meta.description}</div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>

                <button
                  data-send-btn
                  onClick={() => sendMessage()}
                  disabled={(!input.trim() && selectedImages.length === 0) || sending}
                  className="w-9 h-9 rounded-full flex items-center justify-center transition-all disabled:opacity-30 shrink-0"
                  style={{ background: '#00C2A8' }}
                  aria-label="Send message"
                >
                  <Send className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*" multiple
            className="hidden"
            onChange={(e) => {
              const files = e.target.files;
              if (!files) return;
              Array.from(files).slice(0, 10).forEach(file => {
                if (file.size > 50 * 1024 * 1024) { alert('Image must be under 50MB'); return; }
                const reader = new FileReader();
                reader.onload = () => setSelectedImages(prev => [...prev, reader.result as string].slice(0, 10));
                reader.readAsDataURL(file);
              });
              e.target.value = '';
            }}
          />
        </div>
      </div>
    </div>
  );
}
